import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { findIndex } from 'lodash-es';
import * as fromConstants from '../../constants';
import * as fromServices from '../../services';
import * as fromComponents from '../../components';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'find-a-health-center',
  templateUrl: './find-a-health-center.component.html',
  styleUrls: ['./find-a-health-center.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FindAHealthCenterComponent implements OnInit {
  public nameSuggestionResults: fromServices.NameSuggestionResponse = {
    suggestions: []
  };
  public locationData: {
    locations: fromServices.LocationItem[];
    selectedLocation: { lat: string; lon: string; locationName: string };
    radius: number;
  };

  //component properties
  public selectedRadius: string;
  public startIndex: number;
  public endIndex: number;
  public selectedLocation: { lat: string; lon: string; locationName: string };
  public totalResults: number;
  public canShowNameSuggestions = false;
  public displayResults: any = null;
  public setFocus: { lat: number; lon: number; zoom: number; id: number };
  public canShowMap: boolean = false;
  public canShowLoader: boolean = false;
  public showMapAlert: boolean = true;
  public href: string;
  public copyText: string;

  //outputted from other components
  public updateSelectedIndex: number;
  public filterBy: string;
  public selectedId: number;
  public checkboxData?: { name: string; value: string; selected: boolean; }[];
  public canShowPdfExport: boolean;

  public nameSuggestion: string;
  public rows = 6;
  public showInfo = false;
  public inputText: string = '';
  public radiiForLocations: { value: string; displayText: string }[] = fromConstants.FindAHealthCenterRadius;
  public pageTitle: string = fromConstants.PageNames.FindAHealthCenterProgram;
  public subHeaderText: string = fromConstants.SubHeaderText.FindAHealthCenterProgram;
  public contactLink: string = fromConstants.ContactLinks.FindAHealthCenterProgram;
  public homeUrl: string;
  private locationType: fromServices.LocatorTools;

  constructor(
    private toolIdentifierService: fromServices.ToolIdentifierService,
    private nameSuggestionService: fromServices.NameSuggestionService,
    private geoLocationService: fromServices.GeoLocationService,
    private locationStateService: fromServices.LocationStateService,
    private locationFactory: fromServices.LocationFactoryService,
    private apiEnvService: fromServices.ApiEnvironmentService,
    private modalService: fromServices.ModalService
  ) {}

  public ngOnInit(): void {
    this.homeUrl = this.apiEnvService.getHome();
    this.locationType = this.toolIdentifierService.getCurrentTool();
    this.href = window.location.href;
    this.copyText = this.href;
    this.initRadiiForLocations();
    this.initSubHeaderAndTitleText();
    this.checkForLocationParams();
  }

  private checkForLocationParams() {
    const url = window.location.href;
    if (url.includes('?')) {
      const httpParams = new HttpParams({ fromString: url.split('?')[1] });
      const zip = httpParams.get('zip');
      if (zip) this.handleSelectNameSuggestion(zip);
    }
  }

  public handleSelectedIndex(selectedIndex: number): void {
    this.startIndex = selectedIndex * this.rows;
    let endIndex = this.startIndex + this.rows;
    if (endIndex > this.locationData?.locations?.length) {
      endIndex = this.locationData?.locations?.length;
    }
    this.endIndex = endIndex;
    if (this.locationData?.locations?.length) {
      this.displayResults = this.locationData.locations.slice(this.startIndex, this.endIndex);
    }
    this.updateSelectedIndex = selectedIndex;
  }

  public dismissMapAlert() {
    this.showMapAlert = false;
  }

  public getRadiusIndex(): number {
    return findIndex(this.radiiForLocations, radiusItem => {
      return radiusItem.value === this.selectedRadius;
    });
  }

  public increaseRadius(): void {
    let radiusIndex = this.getRadiusIndex();
    radiusIndex++;
    this.selectedRadius = this.radiiForLocations[radiusIndex].value;
    this.getLocationData();
  }

  public shareMail(): void {
    let link =
      'mailto:?subject=HRSA.gov%20' +
      this.pageTitle +
      '&body=Here%20is%20the%20link%20to%20' +
      this.pageTitle +
      '%20on%20HRSA.gov:%20' +
      this.href;
    window.location.assign(link);
  }

  public shareSms(): void {
    let link = 'sms:?&body=Here%20is%20the%20link%20to%20' + this.pageTitle + '%20on%20HRSA.gov:%20' + this.href;
    window.location.assign(link);
  }

  public shareLinkedIn(): void {
    let link = 'https://linkedin.com/shareArticle?title=' + this.pageTitle + '&url=' + this.href;
    window.location.assign(link);
  }

  public shareTwitter(): void {
    let link =
      'https://twitter.com/intent/tweet?ref_src=twsrc%5Etfw%7Ctwcamp%5Ebuttonembed%7Ctwterm%5Eshare%7Ctwgr%5E&amp;url=' +
      this.href;
    window.location.assign(link);
  }

  public shareFacebook(): void {
    let link = 'https://www.facebook.com/sharer/sharer.php?u=' + this.href + '%2F&amp;src=sdkpreparse';
    window.location.assign(link);
  }

  public copyLink(): void {
    navigator.clipboard.writeText(this.href);
    this.copyText = 'Copied!';
    setTimeout(() => {
      this.copyText = this.href;
    }, 3000);
  }

  private initRadiiForLocations(): void {
    switch (this.locationType) {
      case fromServices.LocatorTools.HIVMedicalProvider:
        this.radiiForLocations = fromConstants.HIVMedicalProviderRadius;
        break;
      case fromServices.LocatorTools.HealthCenter:
        this.radiiForLocations = fromConstants.FindAHealthCenterRadius;
        break;
      case fromServices.LocatorTools.HealthCenterControlledNetwork:
        this.radiiForLocations = fromConstants.FindANetworkRadius;
        break;
      case fromServices.LocatorTools.MigrantSeasonalHeadStart:
        this.radiiForLocations = fromConstants.HdsLocatorRadius;
        break;
      case fromServices.LocatorTools.OrganTransplantCenter:
        this.radiiForLocations = fromConstants.OrganTransplantCenterRadius;
        break;
    }
    this.selectedRadius = this.radiiForLocations[0].value;
  }

  private initSubHeaderAndTitleText(): void {
    switch (this.locationType) {
      case fromServices.LocatorTools.HIVMedicalProvider:
        this.subHeaderText = fromConstants.SubHeaderText.HivProgram;
        this.pageTitle = fromConstants.PageNames.HivProgram;
        this.contactLink = fromConstants.ContactLinks.HivProgram;
        break;
      case fromServices.LocatorTools.HealthCenter:
        this.subHeaderText = fromConstants.SubHeaderText.FindAHealthCenterProgram;
        this.pageTitle = fromConstants.PageNames.FindAHealthCenterProgram;
        this.contactLink = fromConstants.ContactLinks.FindAHealthCenterProgram;
        break;
      case fromServices.LocatorTools.HealthCenterControlledNetwork:
        this.subHeaderText = fromConstants.SubHeaderText.FindANetworkProgram;
        this.pageTitle = fromConstants.PageNames.FindANetworkProgram;
        this.contactLink = fromConstants.ContactLinks.FindANetworkProgram;
        break;
      case fromServices.LocatorTools.MigrantSeasonalHeadStart:
        this.subHeaderText = fromConstants.SubHeaderText.MigrantHeadStartProgram;
        this.pageTitle = fromConstants.PageNames.MigrantHeadStartProgram;
        this.contactLink = fromConstants.ContactLinks.MigrantHeadStartProgram;
        break;
      case fromServices.LocatorTools.OrganTransplantCenter:
        this.subHeaderText = fromConstants.SubHeaderText.OrganTransplantProgram;
        this.pageTitle = fromConstants.PageNames.OrganTransplantProgram;
        this.contactLink = fromConstants.ContactLinks.OrganTransplantProgram;
        break;
    }
  }

  private insertString(mainString, stringToInsert, index) {
    return mainString.slice(0, index) + stringToInsert + mainString.slice(index);
  }

  private nameSuggestionTest(): void {
    const location: fromServices.NameServiceLocation = {
      spatialReference: {
        latestWkid: 3857,
        wkid: 102100
      }
    };
    this.nameSuggestionService.getNameSuggestions(this.nameSuggestion, location).then(results => {
      results?.suggestions.forEach(resultItem => {
        let formattedText = '';
        const lowerCaseText = resultItem.text.toLocaleLowerCase();
        const startIndex = lowerCaseText.indexOf(this.nameSuggestion.toLowerCase());
        const endIndex = this.nameSuggestion.length - 1;
        if (startIndex > -1) {
          formattedText = this.insertString(resultItem.text, '<strong>', startIndex);
          formattedText = this.insertString(formattedText, '</strong>', endIndex + '<strong>'.length + 1);
        } else {
          formattedText = resultItem.text;
        }
        return (resultItem.formattedText = formattedText);
      });
      this.nameSuggestionResults = results;
    });
  }

  public handleSearch(): void {
    this.canShowNameSuggestions = false;
    this.filterBy = null;
    this.nameSuggestionResults = { suggestions: [] };
    if (this.nameSuggestion) {
      this.getGeoLocation();
    }
  }

  public handleNameSuggestion(): void {
    if (this.nameSuggestion) {
      this.canShowNameSuggestions = true;
      this.nameSuggestionTest();
    } else {
      this.canShowNameSuggestions = false;
      this.nameSuggestionResults = { suggestions: [] };
    }
  }

  public handleSelectNameSuggestion(selectedNameSuggestion: string): void {
    this.filterBy = null;
    this.nameSuggestion = selectedNameSuggestion;
    this.canShowNameSuggestions = false;
    this.nameSuggestionResults = { suggestions: [] };
    this.getGeoLocation();
  }

  private getGeoLocation(): void {
    const Window: any = window;
    Window.dataLayer.push({
      event: 'gtm.click',
      onSearch: this.nameSuggestion
    });
    this.geoLocationService.getGeoLocation(this.nameSuggestion).then(geoLocationResponse => {
      if (geoLocationResponse.issuccessful) {
        this.selectedLocation = {
          lat: geoLocationResponse?.latitude,
          lon: geoLocationResponse?.longitude,
          locationName: this.nameSuggestion
        };
        this.canShowMap = false;
        this.displayResults = null;
        this.getLocationData();
      } else {
        this.displayResults = [];
      }
    });
  }

  private getLocationData(): void {
    this.totalResults = null;
    let getStartIndex = findIndex(this.radiiForLocations, radiusItem => {
      return radiusItem.value === this.selectedRadius;
    });
    const request: fromServices.LocationsRequest = {
      lon: this.selectedLocation.lon,
      lat: this.selectedLocation.lat,
      radius: this.selectedRadius,
      heathCenter: this.filterBy,
      checkBoxData: this.checkboxData
    };
    this.canShowLoader = true;
    this.locationFactory.getLocations(request).then(response => {
      let startRadius = this.radiiForLocations[getStartIndex].value;
      if (response && response.length) {
        this.locationStateService.Locations = response;
        this.canShowPdfExport = true;
        this.locationData = {
          locations: response,
          selectedLocation: this.selectedLocation,
          radius: parseInt(startRadius, 10)
        };
        this.totalResults = this.locationData?.locations?.length;
        this.updateSelectedIndex = 0;
        this.initLocationData();
      } else {
        this.displayResults = [];
      }
      this.canShowLoader = false;
    });
  }

  private initLocationData(): void {
    if (this.locationData.locations) {
      this.canShowMap = true;
      this.locationData.locations.forEach((resultItem, index) => {
        this.locationData.locations[index].index = index + 1;
        this.locationData.locations[index].directionsUrl = encodeURI(
          `${fromConstants.UrlConstants.GoogleMaps}${this.selectedLocation.lat},${this.selectedLocation.lon}/${resultItem.CtrAddress} ${resultItem.CtrCity} ${resultItem.CtrStateAbbr} ${resultItem.CtrZipCd}/`
        );
        // Looks like either UrlTxt or SiteUrl may be populated; we'll try them both.
        const locationUrl = !!this.locationData.locations[index].UrlTxt
          ? this.locationData.locations[index].UrlTxt
          : this.locationData.locations[index].SiteUrl;

        // Looks like some of the Urls are properly formatted some aren't; we'll format them if needed.
        if (!!locationUrl && locationUrl.indexOf('http') === -1) {
          this.locationData.locations[index].UrlTxt = 'https://' + locationUrl;
        }

        if (locationUrl && locationUrl.toLocaleLowerCase().indexOf('[no data]') > -1) {
          this.locationData.locations[index].UrlTxt = null;
        }
      });
      this.startIndex = 0;
      this.endIndex = this.locationData.locations.length > this.rows ? this.rows : this.locationData.locations.length;
      this.displayResults = this.locationData.locations.slice(0, this.endIndex);
    }
  }

  public handleRadiusChange(): void {
    const Window: any = window;
    Window.dataLayer.push({
      event: 'onRadiusChange',
      radius: this.selectedRadius
    });
    if (this.nameSuggestion) {
      this.canShowNameSuggestions = false;
      this.nameSuggestionResults = { suggestions: [] };
      this.canShowMap = false;
      this.getLocationData();
    }
  }

  public handleSelectedMarker(id: number): void {
    this.selectedId = id;
    const match = this.locationData.locations.find(locationItem => {
      return locationItem.Id === id;
    });
    if (match) {
      let nearestResult = 0;
      if (match.index <= this.rows) {
        // Start at beginning
        nearestResult = 0;
      } else {
        nearestResult = Math.floor(match.index / this.rows);
      }
      this.startIndex = nearestResult > 0 ? nearestResult * this.rows : 0;
      this.endIndex =
        this.startIndex + this.rows < this.locationData.locations.length
          ? this.startIndex + this.rows
          : this.locationData.locations.length;
      this.displayResults = this.locationData.locations.slice(this.startIndex, this.endIndex);
      this.updateSelectedIndex = nearestResult;
    }
  }

  public handleFilterBy(event: { filterBy: string; checkboxData?: { name: string; value: string; selected: boolean; }[] }): void {
    this.filterBy = event.filterBy;
    this.checkboxData = event.checkboxData;
    this.locationStateService.HealthCenter = this.filterBy;
    this.locationStateService.CheckboxData = this.checkboxData;
    this.getLocationData();
  }

  public handleStartOver(): void {
    this.nameSuggestion = null;
    this.locationData = null;
    this.updateSelectedIndex = null;
    this.selectedLocation = null;
    this.totalResults = null;
    this.selectedRadius = this.radiiForLocations[0].value;
    this.canShowNameSuggestions = false;
    this.displayResults = null;
    this.startIndex = null;
    this.endIndex = null;
    this.selectedId = null;
    this.inputText = null;
    this.filterBy = null;
    this.checkboxData = null;
    this.setFocus = null;
    this.locationStateService.Locations = null;
    this.locationStateService.Latitude = null;
    this.locationStateService.Zoom = null;
    this.locationStateService.Url = null;
    this.locationStateService.CheckboxData = null;
    this.locationStateService.HealthCenter = null;
    this.locationStateService.Radius = parseInt(this.radiiForLocations[0].value);
    this.canShowMap = false;

    this.canShowPdfExport = false;
    window.scrollTo(0, 0);
  }

  public handleAddressFocus(result: fromServices.LocationItem): void {
    this.selectedId = result.Id;
    this.setFocus = { lat: result.Latitude, lon: result.Longitude, zoom: 16, id: result.Id };
  }

  public openExternalLinkDisclaimer(url: string): void {
    this.modalService.open(fromComponents.ExternalLinkDisclaimerComponent, { targetUrl: url });
  }
}
